<script>
import { mapState } from 'vuex'
import { formatPercentage } from '@/utils/format-number'

export default {
    name: 'InventoryStatusCalculator',
    data() {
        return {
            formatPercentage,
            isCharges: false,
            showPopper: false,
            isShipping: false,
            isAmazonFees: false,
            isPromotions: false,
        }
    },
    computed: {
        ...mapState('inventory', ['stats']),
    },
    methods: {
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        isHyphenClass(value) {
            if (value === 0) {
                return 'text-muted'
            } else if (value < 0) {
                return 'text-danger'
            }
            return 'text-success'
        },
    },
}
</script>

<template>
    <Popper class="light-popper" style="position: absolute; top: 9.27px; right: 9.67px" arrow :show="showPopper">
        <template #content>
            <div class="d-flex flex-column" style="min-width: 340px">
                <div class="d-flex align-items-center justify-content-between" style="height: 53px">
                    <img :src="`/logo.svg?v=${new Date().getTime()}`" width="100" alt="Logo" />
                    <button type="button" class="btn btn-link text-secondary p-0" @click="showPopper = !showPopper"><i class="bi bi-x" style="font-size: 24px"></i></button>
                </div>
                <table class="table table-bordered modal-table" style="width: 360px; margin-left: -10px; margin-right: -10px; margin-bottom: -6px !important">
                    <tbody>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.units') }}</td>
                            <td style="text-align: right">
                                <strong>{{ stats?.tooltip?.total_units }}</strong>
                            </td>
                        </tr>
                        <tr style="cursor: pointer" @click="isCharges = !isCharges">
                            <td>
                                <div class="d-flex align-items-center" style="gap: 10px">
                                    <i v-if="stats?.tooltip?.charges && Object.keys(stats?.tooltip?.charges).length > 0" :class="isCharges ? 'bi-dash-lg' : 'bi-plus-lg text-primary'" style="font-size: 14px"></i>
                                    <span>{{ $t('views.inventoryStatus.calculator.columns.total_order_amount') }}</span>
                                </div>
                            </td>
                            <td style="text-align: right">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.total_charges) }}</strong>
                            </td>
                        </tr>
                        <template v-if="isCharges">
                            <tr
                                class="bg-light"
                                v-for="item in Object.keys(stats?.tooltip?.charges).sort((a, b) => {
                                    if (a === 'Principal') return -1
                                    if (b === 'Principal') return 1
                                    if (a === 'Tax') return -1
                                    if (b === 'Tax') return 1

                                    // Tax içeren keyleri ana keylerinin hemen ardına yerleştir
                                    const aTax = a.endsWith('Tax')
                                    const bTax = b.endsWith('Tax')
                                    if (aTax !== bTax) {
                                        if (aTax && b === a.replace('Tax', '')) return 1
                                        if (bTax && a === b.replace('Tax', '')) return -1
                                    }

                                    return a.localeCompare(b)
                                })"
                                :key="item"
                            >
                                <td style="padding-left: 38px">
                                    {{ $t(`views.orders.financeLabels.` + item) }}
                                </td>
                                <td style="text-align: right">
                                    {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.charges[item]) }}</strong>
                                </td>
                            </tr>
                        </template>
                        <tr style="cursor: pointer" @click="isAmazonFees = !isAmazonFees">
                            <td>
                                <div class="d-flex align-items-center" style="gap: 10px">
                                    <i v-if="stats?.tooltip?.fees && Object.keys(stats?.tooltip?.fees).length > 0" :class="isAmazonFees ? 'bi-dash-lg' : 'bi-plus-lg text-primary'" style="font-size: 14px"></i>
                                    <span style="text-transform: uppercase">
                                        {{ $t(`views.orders.detail.salesSummary.columns.fees`) }}
                                    </span>
                                </div>
                            </td>
                            <td style="text-align: right">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.total_fees) }}</strong>
                            </td>
                        </tr>
                        <template v-if="isAmazonFees">
                            <tr v-for="item in Object.keys(stats?.tooltip?.fees).sort()" :key="item" class="bg-light">
                                <td style="padding-left: 38px">
                                    {{ $t(`views.orders.financeLabels.` + item) }}
                                </td>
                                <td style="text-align: right">
                                    {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.fees[item]) }}</strong>
                                </td>
                            </tr>
                        </template>
                        <tr style="cursor: pointer" @click="isPromotions = !isPromotions">
                            <td>
                                <div class="d-flex align-items-center" style="gap: 10px">
                                    <i v-if="stats?.tooltip?.promotions && Object.keys(stats?.tooltip?.promotions).length > 0" :class="isPromotions ? 'bi-dash-lg' : 'bi-plus-lg text-primary'" style="font-size: 14px"></i>
                                    <span style="text-transform: uppercase">
                                        {{ $t(`views.orders.detail.salesSummary.columns.promos`) }}
                                    </span>
                                </div>
                            </td>
                            <td style="text-align: right">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.total_promotions) }}</strong>
                            </td>
                        </tr>
                        <template v-if="isPromotions">
                            <tr v-for="item in Object.keys(stats?.tooltip?.promotions).sort()" :key="item" class="bg-light">
                                <td style="padding-left: 38px">
                                    {{ $t(`views.orders.financeLabels.` + item) }}
                                </td>
                                <td style="text-align: right">
                                    {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.promotions[item]) }}</strong>
                                </td>
                            </tr>
                        </template>
                        <tr>
                            <td style="text-transform: uppercase">{{ $t(`views.orders.detail.salesSummary.columns.taxWithheld`) }}</td>
                            <td style="text-align: right">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.total_taxwitheld) }}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.cog') }}</td>
                            <td style="text-align: right">
                                {{ stats?.sourceCurrency }} <strong>{{ formatNumber(stats?.tooltip?.cost_of_goods) }}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.revenue') }}</td>
                            <td style="text-align: right">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.revenue) }}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.profit') }}</td>
                            <td style="text-align: right" :class="isHyphenClass(stats?.tooltip?.profit)">
                                {{ stats?.targetCurrency }} <strong>{{ formatNumber(stats?.tooltip?.profit) }}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.roi') }}</td>
                            <td style="text-align: right">
                                <strong :class="isHyphenClass(stats?.tooltip?.roi)"> <i :class="[stats?.tooltip?.margin > 0 ? 'bi-graph-up' : 'bi-graph-down']"></i> {{ formatPercentage(stats?.tooltip?.roi || 0) }} </strong>
                            </td>
                        </tr>
                        <tr>
                            <td>{{ $t('views.inventoryStatus.calculator.columns.margin') }}</td>
                            <td style="text-align: right">
                                <strong :class="isHyphenClass(stats?.tooltip?.margin)"> <i :class="[stats?.tooltip?.margin > 0 ? 'bi-graph-up' : 'bi-graph-down']"></i> {{ formatPercentage(stats?.tooltip?.margin || 0) }} </strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <button type="button" class="btn btn-link text-secondary p-0" style="line-height: 1"><i class="bi bi-file-text" @click="showPopper = !showPopper"></i></button>
    </Popper>
</template>

<style lang="scss" scoped>
.modal-table {
    margin-bottom: 0 !important;
    border-radius: 5px;

    tbody {
        tr {
            td {
                width: 170px;
            }
            td:first-child {
                font-weight: 600;
            }
        }
    }
}
</style>
